<template>
  <v-container fluid class="na-page">
    <v-card class="na-card" flat>
      <div class="na-title">New Employee Activity</div>
      <div class="na-subtitle">
        Add details about the employee's work activity. Fields marked with <span class="na-req">*</span> are required.
      </div>

      <v-row class="mt-6">
        <!-- Employee -->
        <v-col cols="12" md="6" class="na-col">
          <label class="na-label">Employee <span class="na-req">*</span></label>
          <v-autocomplete v-model="dataItem.userId" :items="Emp_list" item-text="name" item-value="id"
            placeholder="Select employee" prepend-inner-icon="mdi-account-outline" outlined dense hide-details
            class="na-input"></v-autocomplete>
        </v-col>

        <!-- Assignment / Case -->
        <v-col cols="12" md="6" class="na-col">
          <label class="na-label">Assignment / Case <span class="na-req">*</span></label>
          <v-autocomplete v-model="dataItem.jobtitle_id" :items="jobtitle_list" item-text="name" item-value="id"
            placeholder="Select assignment/case" prepend-inner-icon="mdi-folder-outline" outlined dense hide-details
            class="na-input"></v-autocomplete>
        </v-col>

        <!-- Client -->
        <v-col cols="12" md="6" class="na-col">
          <label class="na-label">Client <span class="na-req">*</span></label>
          <v-autocomplete v-model="dataItem.customer_id" :items="customer_list" item-text="name" item-value="id"
            placeholder="Select client" prepend-inner-icon="mdi-office-building-outline" outlined dense hide-details
            class="na-input" @click:clear="
              $nextTick(() => {
                dataItem.customer_id = 0;
              })
              "></v-autocomplete>
        </v-col>

        <!-- Charge Type -->
        <v-col cols="12" md="6" class="na-col">
          <label class="na-label">Charge Type <span class="na-req">*</span></label>
          <v-autocomplete v-model="dataItem.chargetype" :items="charge_list" item-text="name" item-value="value"
            placeholder="Select charge type" prepend-inner-icon="mdi-tag-outline" outlined dense hide-details
            class="na-input"></v-autocomplete>
        </v-col>

        <!-- Date / Start / End -->
        <v-col cols="12" md="6" class="na-col">
          <div class="na-time-row">
            <div class="na-date">
              <label class="na-label">Date <span class="na-req">*</span></label>
              <v-menu v-model="menuDateCheckin" :close-on-content-click="false" transition="scale-transition" offset-y
                min-width="auto">
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field v-model="computedDateCheckInFormatted" append-icon="mdi-calendar-blank-outline" readonly
                    v-bind="attrs" v-on="on" outlined dense hide-details class="na-input"></v-text-field>
                </template>
                <v-date-picker v-model="dataItem.checkin" :max="new Date(
                  Date.now() - new Date().getTimezoneOffset() * 60000,
                )
                  .toISOString()
                  .substr(0, 10)
                  " :min="minDateAllowed" @input="menuDateCheckin = false" @change="getWorkHourDuration()" />
              </v-menu>
            </div>

            <div class="na-time">
              <label class="na-label">Start Time <span class="na-req">*</span></label>
              <div class="na-time-box" :class="{ 'na-time-box--disabled': isDisabledTimeField }">
                <datetime class="na-dt" type="time" input-id="menutimeadd" value-zone="Asia/Bangkok" format="HH:mm"
                  placeholder="--:--" v-model="dataItem.timecheckin" :max-datetime="getMaxTime()"
                  :disabled="isDisabledTimeField" @input="getWorkHourDuration" />
                <v-icon class="na-time-icon">mdi-clock-outline</v-icon>
              </div>
            </div>

            <div class="na-time">
              <label class="na-label">End Time <span class="na-req">*</span></label>
              <div class="na-time-box" :class="{ 'na-time-box--disabled': isDisabledTimeFieldCheckOut }">
                <datetime class="na-dt" type="time" input-id="menutimeaddout" value-zone="Asia/Bangkok"
                  format="HH:mm" placeholder="--:--" v-model="dataItem.timecheckout"
                  :max-datetime="getMaxTimeCheckOut()" :disabled="isDisabledTimeFieldCheckOut"
                  @input="getWorkHourDuration()"></datetime>
                <v-icon class="na-time-icon">mdi-clock-outline</v-icon>
              </div>
            </div>
          </div>

          <div v-if="showDuration != ''" class="na-duration mt-4">
            <span class="na-label mb-0">Duration:</span>
            <span :class="showDuration != 'Start date/time must be before End date/time'
              ? 'na-ok'
              : 'na-err'
              ">{{ showDuration }}</span>
          </div>
        </v-col>

        <!-- Details -->
        <v-col cols="12" md="6" class="na-col">
          <label class="na-label">Details <span class="na-req">*</span></label>
          <v-textarea v-model="dataItem.detail" placeholder="Add activity details, notes, or description..." outlined
            hide-details rows="5" class="na-input na-textarea"></v-textarea>
          <div class="na-counter">{{ (dataItem.detail || "").length.toLocaleString() }}/1,000</div>
        </v-col>
      </v-row>

      <v-divider class="mt-6 mb-6"></v-divider>

      <div class="d-flex justify-center" style="gap: 12px">
        <v-btn depressed width="124" height="42" class="na-btn-save" @click="save()">Save</v-btn>
        <v-btn to="/my-activity" depressed outlined width="124" height="42" class="na-btn-cancel">Cancel</v-btn>
      </div>
    </v-card>

    <SuccessDialog :status="dialogAdd" :text_color="text_color" :title="title" :message="message" />

    <SuccessDialogPush :status="dialogAdd" :text_color="text_color" :title="title" :message="message" :link="link" />
  </v-container>
</template>

<script>
import api from "@/services/api";
import { server } from "../services/constants";
import StockCard from "@/components/cards/StockCard";
import SuccessDialog from "@/components/cards/SuccessDialog";
import SuccessDialogPush from "@/components/cards/SuccessDialogPush";
import {
  tolocalestringnumber,
  stringcommatonumber,
} from "./computing/tolocalestringnumber";
import unity from "@/unity/unity";
const moment = require("moment");

export default {
  name: "New Activity",
  data() {
    return {
      dialogAdd: false,
      dialogSendMail: false,
      text_color: "text-h5 green--text text-center",
      title: "green",
      message: "green",
      link: "",
      Emp_list: [],
      jobtitle_list: [],
      charge_list: [
        { value: "T/C", name: "Time Charge" },
        { value: "N/C", name: "No Charge" },
        { value: "F/C", name: "Fix Charge" },
        { value: "OFF", name: "ทำงานออฟฟิต" },
      ],
      customer_list: [],
      menuDateCheckin: false,
      menuDateCheckout: false,
      dataItem: {
        userId: 0,
        customer_id: 0,
        jobtitle_id: 0,
        // checkin: "",
        // checkout: "",
        checkin: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
          .toISOString()
          .substr(0, 10),
        checkout: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
          .toISOString()
          .substr(0, 10),
        timecheckin: "",
        timecheckout: "",
        timecheckinadd: "",
        timecheckoutadd: "",
        hours: "",
        chargetype: "",
        prospect: "",
        detail: "",
        durationhour: 0,
        durationminute: 0,
      },
      addItem: {
        chargeType: "",
        images: "",
        company_id: 0,
        isCheckedOut: 1,
        userId: 0,
        departmentId: 0,
        jobTitleId: 0,
        customerId: 0,
        prospect: "",
        detail: "",
        status: "A",
        caseType: null,
        check_auto: 0,
        addby: "W",
      },
      showDuration: "",
    };
  },
  components: {
    StockCard,
    SuccessDialog,
    SuccessDialogPush,
  },
  computed: {
    computedDateCheckInFormatted() {
      return unity.formatDate(this.dataItem.checkin);
    },
    computedDateCheckOutFormatted() {
      return unity.formatDate(this.dataItem.checkout);
    },
    minDateAllowed() {
      return moment()
        .tz("Asia/Bangkok")
        .subtract(5, "days")
        .format("YYYY-MM-DD");
    },
    isTodayCheckin() {
      const selected = new Date(this.dataItem.checkin);
      const today = new Date();
      return (
        selected.toISOString().slice(0, 10) === today.toISOString().slice(0, 10)
      );
    },
    isDisabledTimeField() {
      return this.isTodayCheckin && this.isPastCurrentTimeLimit();
    },
    isTodayCheckOut() {
      const selected = new Date(this.dataItem.checkout);
      const today = new Date();
      return (
        selected.toISOString().slice(0, 10) === today.toISOString().slice(0, 10)
      );
    },
    isDisabledTimeFieldCheckOut() {
      return this.isTodayCheckOut && this.isPastCurrentTimeLimitCheckOut();
    },
  },

  watch: {
    "dataItem.checkin"(newVal) {
      const today = moment.tz("Asia/Bangkok").format("YYYY-MM-DD");

      if (newVal === today && this.dataItem.timecheckin) {
        const time = moment.tz(this.dataItem.timecheckin, "Asia/Bangkok");
        const now = moment.tz("Asia/Bangkok");

        if (time.isAfter(now)) {
          this.dataItem.timecheckin = null;
        }
      }
    },
    "dataItem.checkout"(newVal) {
      const today = moment.tz("Asia/Bangkok").format("YYYY-MM-DD");

      if (newVal === today && this.dataItem.timecheckout) {
        const time = moment.tz(this.dataItem.timecheckout, "Asia/Bangkok");
        const now = moment.tz("Asia/Bangkok");

        if (time.isAfter(now)) {
          this.dataItem.timecheckout = null;
        }
      }
    },
  },

  async mounted() {
    await api.checkVersion();
    this.loadEmp();
    this.loadCustomer();
    this.loadJobtitle();
    this.$hideLoader();
    this.$forceUpdate();
    const userId = localStorage.getItem(server.USER_ID);
    if (userId && api.isLoggedIn()) {
      // this.$router.push("/");
    } else {
      this.$store.state.isLogged = false;
      this.$router.push("/login");
    }
  },
  methods: {
    getMaxTime() {
      const selected = moment
        .tz(this.dataItem.checkin, "Asia/Bangkok")
        .format("YYYY-MM-DD");
      const today = moment.tz("Asia/Bangkok").format("YYYY-MM-DD");

      if (selected === today) {
        return moment.tz("Asia/Bangkok").format("HH:mm");
      }
      return null;
    },
    getMaxTimeCheckOut() {
      const selected = moment
        .tz(this.dataItem.checkout, "Asia/Bangkok")
        .format("YYYY-MM-DD");
      const today = moment.tz("Asia/Bangkok").format("YYYY-MM-DD");

      if (selected === today) {
        return moment.tz("Asia/Bangkok").format("HH:mm");
      }
      return null;
    },
    isPastCurrentTimeLimit() {
      // Optional: ใช้ได้ถ้าต้องการเงื่อนไขซับซ้อนกว่านี้
      return false;
    },
    isPastCurrentTimeLimitCheckOut() {
      // Optional: ใช้ได้ถ้าต้องการเงื่อนไขซับซ้อนกว่านี้
      return false;
    },
    async loadEmp() {
      const result = await api.getAccountslist_by_ComID(
        localStorage.getItem(server.COMPANYID),
      );

      this.Emp_list = result.data;
    },
    async loadCustomer() {
      const result = await api.getAllCustomerListactiveCompany({
        params: {
          company_id: localStorage.getItem(server.COMPANYID),
        },
      });
      this.customer_list = result.data;
    },
    async loadJobtitle() {
      const result = await api.getAllJobtitleByCompany(
        localStorage.getItem(server.COMPANYID),
      );

      this.jobtitle_list = result.data;
    },
    async getWorkHourDuration() {
      if (this.dataItem.checkout != "") {
        this.dataItem.checkout = this.dataItem.checkin;
        if (
          this.dataItem.timecheckin !== "" &&
          this.dataItem.timecheckout !== ""
        ) {
          // ดึงเฉพาะเวลา (HH:mm) จาก string ISO
          const checkinTime = moment(this.dataItem.timecheckin).format("HH:mm");
          const checkoutTime = moment(this.dataItem.timecheckout).format(
            "HH:mm",
          );

          // สร้าง moment ใหม่โดยใช้วันที่เดียวกัน เช่น 2000-01-01 เพื่อไม่ให้วันมีผล
          const baseDate = "2000-01-01";
          const checkin = moment(
            `${baseDate} ${checkinTime}`,
            "YYYY-MM-DD HH:mm",
          );
          const checkout = moment(
            `${baseDate} ${checkoutTime}`,
            "YYYY-MM-DD HH:mm",
          );

          if (checkin.isSameOrAfter(checkout)) {
            this.showDuration = "Start date/time must be before End date/time";
            return;
          }

          let totalMinutes = checkout.diff(checkin, "minutes");

          // ถ้า checkout < checkin แปลว่าเวลาข้ามคืน
          if (totalMinutes < 0) {
            totalMinutes += 1440; // เพิ่ม 24 ชั่วโมง (1440 นาที)
          }

          const hours = Math.floor(totalMinutes / 60);
          const minutes = totalMinutes % 60;

          if (hours > 0) {
            if (minutes > 0) {
              this.showDuration = `${hours} Hours ${minutes} Minute`;
            } else {
              this.showDuration = `${hours} Hours`;
            }
          } else {
            this.showDuration = `${minutes} Minute`;
          }
        }
      }
    },
    async save() {
      if (this.dataItem.userId == "" || this.dataItem.userId == null) {
        this.$store.state.global_dialog = true;
        this.setupAlertDialog(
          true,
          "Check your information again",
          "กรุณากรอกรายละเอียด Employee ",
          "text-h5 red--text text-center",
        );
        return;
      }

      if (
        this.dataItem.jobtitle_id == "" ||
        this.dataItem.jobtitle_id == null
      ) {
        this.$store.state.global_dialog = true;
        this.setupAlertDialog(
          true,
          "Check your information again",
          "กรุณากรอกรายละเอียด Work Case ",
          "text-h5 red--text text-center",
        );
        return;
      }

      if (this.dataItem.checkin == "") {
        this.$store.state.global_dialog = true;
        this.setupAlertDialog(
          true,
          "Check your information again",
          "กรุณากรอกรายละเอียด Date",
          "text-h5 red--text text-center",
        );
        return;
      } else {
        this.dataItem.checkout = this.dataItem.checkin;
      }

      if (this.dataItem.timecheckin == "") {
        this.$store.state.global_dialog = true;
        this.setupAlertDialog(
          true,
          "Check your information again",
          "กรุณากรอกรายละเอียด Start Time",
          "text-h5 red--text text-center",
        );
        return;
      }
      if (this.dataItem.timecheckout == "") {
        this.$store.state.global_dialog = true;
        this.setupAlertDialog(
          true,
          "Check your information again",
          "กรุณากรอกรายละเอียด End Time",
          "text-h5 red--text text-center",
        );
        return;
      }
      if (this.dataItem.chargetype == "") {
        this.$store.state.global_dialog = true;
        this.setupAlertDialog(
          true,
          "Check your information again",
          "กรุณากรอกรายละเอียด Charge-Type ",
          "text-h5 red--text text-center",
        );
        return;
      }

      if (this.dataItem.detail == "") {
        this.$store.state.global_dialog = true;
        this.setupAlertDialog(
          true,
          "Check your information again",
          "กรุณากรอกรายละเอียด Details ",
          "text-h5 red--text text-center",
        );
        return;
      }

      const checkinTime = moment(this.dataItem.timecheckin).format("HH:mm");
      const checkoutTime = moment(this.dataItem.timecheckout).format("HH:mm");
      const baseDate = "2000-01-01";
      const checkin = moment(`${baseDate} ${checkinTime}`, "YYYY-MM-DD HH:mm");
      const checkout = moment(
        `${baseDate} ${checkoutTime}`,
        "YYYY-MM-DD HH:mm",
      );
      const checkinFull = moment(
        `${this.dataItem.checkin} ${moment(this.dataItem.timecheckin).format("HH:mm:ss")}`,
        "YYYY-MM-DD HH:mm:ss"
      ).add(7, 'hours');
      if (checkin.isSameOrAfter(checkout)) {
        this.$store.state.global_dialog = true;
        this.setupAlertDialog(
          true,
          "Check your information again",
          "Start date/time must be before End date/time",
          "text-h5 red--text text-center",
        );
        return;
      }
      let totalMinutes = checkout.diff(checkin, "minutes");
      var departmentIduser = 0;
      try {
        var res = await api.getAccountByid(Number(this.dataItem.userId))
        departmentIduser = Number(res.data[0].dep_id)
      } catch (error) {
        console.error(error)
      }

      var addItem = {};
      addItem.chargeType = this.dataItem.chargetype;
      addItem.company_id = Number(localStorage.getItem(server.COMPANYID));
      addItem.checkInDate = checkinFull.format("YYYY-MM-DD HH:mm:ss");
      addItem.checkOutDate = `${this.dataItem.checkout} ${moment(
        this.dataItem.timecheckout,
      ).format("HH:mm:ss")}`;
      addItem.isCheckedOut = true;
      addItem.userId = this.dataItem.userId;
      addItem.departmentId = departmentIduser;
      addItem.jobTitleId = this.dataItem.jobtitle_id;
      addItem.customerId = this.dataItem.customer_id;
      addItem.prospect = this.dataItem.prospect;
      addItem.detail = this.dataItem.detail.replace(/'/g, "\\'");
      addItem.duration = Number(totalMinutes);
      addItem.addby = "W";
      addItem.check_auto = 0;
      addItem.status = "A";
      addItem.type = "A"; // ✅ เพิ่มตรงนี้

      this.$showLoader();
      const res_updateAct = await api.createActivityEmp(addItem);
      this.$hideLoader();
      if (res_updateAct.status == 200 || res_updateAct.status == 201) {
        this.$store.state.global_push_dialog = true;
        this.setupAlertDialogPush(
          true,
          "Information Saved",
          "Information successfully saved",
          "text-h5 green--text text-center",
          "/my-activity",
        );
      } else {
        this.$store.state.global_dialog = true;
        this.setupAlertDialog(
          true,
          "Information Saved",
          "ไม่สามารถบันทึกได้",
          "text-h5 red--text text-center",
        );
        return;
      }
    },
    setupAlertDialogPush(status, title, message, text_color, link) {
      this.title = title;
      this.message = message;
      this.dialogAdd = status;
      this.text_color = text_color;
      this.link = link;
    },
    setupAlertDialog(status, title, message, text_color) {
      this.title = title;
      this.message = message;
      this.dialogAdd = status;
      this.text_color = text_color;
    },
  },
  async beforeCreate() {
    console.log("beforeCreate");
    this.$store.state.navMenu = true;
  },
  async created() { },
};
</script>

<style scoped>
.na-page { background: #f4f6f9; }
.na-card { border-radius: 12px !important; padding: 36px 52px 32px; }
.na-title { font-size: 28px; font-weight: 800; color: #1a2440; line-height: 1.2; }
.na-subtitle { font-size: 14px; color: #6b7280; margin-top: 6px; }
.na-req { color: #e53935; }
.na-col { padding-top: 10px; padding-bottom: 14px; }
.na-col:nth-child(odd) { padding-right: 24px; }
.na-col:nth-child(even) { padding-left: 24px; }
.na-label { display: block; font-size: 15px; font-weight: 700; color: #374151; margin-bottom: 8px; }

.na-input >>> .v-input__slot { min-height: 48px !important; border-radius: 6px; background: #fff; }
.na-input >>> fieldset { border-color: #c9ced6; }
.na-input >>> .v-input__prepend-inner { margin-top: 12px !important; margin-right: 10px; }
.na-input >>> .v-input__prepend-inner .v-icon,
.na-input >>> .v-input__append-inner .v-icon { color: #4b5563; }
.na-input >>> .v-input__append-inner { margin-top: 12px !important; }
.na-input >>> input::placeholder,
.na-input >>> textarea::placeholder { color: #9ca3af; }
.na-textarea >>> .v-input__slot { min-height: 150px !important; }
.na-counter { text-align: right; font-size: 13px; color: #6b7280; margin-top: 6px; }

.na-time-row { display: flex; gap: 28px; flex-wrap: wrap; }
.na-date { flex: 1 1 200px; max-width: 200px; }
.na-time { flex: 0 1 136px; }
.na-time-box { position: relative; height: 48px; border: 1px solid #c9ced6; border-radius: 6px; background: #fff; }
.na-time-box:hover { border-color: #1f2d4d; }
.na-time-box--disabled { background: #f3f4f6; }
.na-time-icon { position: absolute !important; right: 12px; top: 12px; pointer-events: none; color: #4b5563 !important; }
.na-dt { height: 100%; }
.na-dt >>> input { width: 100%; height: 46px; padding: 0 40px 0 16px; border: none; outline: none; background: transparent; cursor: pointer; font-size: 15px; color: #1f2937; }
.na-dt >>> input::placeholder { color: #9ca3af; }

.na-duration { display: flex; align-items: center; gap: 8px; }
.na-ok { color: #16a34a; font-weight: 600; }
.na-err { color: #dc2626; font-weight: 600; }

.na-btn-save { background-color: #1a2440 !important; color: #fff !important; font-weight: 700; letter-spacing: 1px; border-radius: 6px; }
.na-btn-cancel { border-color: #1a2440 !important; color: #1a2440 !important; font-weight: 700; letter-spacing: 1px; border-radius: 6px; }

@media (max-width: 960px) {
  .na-card { padding: 24px 18px; }
  .na-col:nth-child(odd), .na-col:nth-child(even) { padding-left: 12px; padding-right: 12px; }
  .na-date { max-width: none; }
}
</style>